// Check-Up-Anfragen im Kalender (15.10.2026, backend/checkup_kalender.py): offene Anfragen am Wunschtag freigeben oder
// ablehnen, bestätigte Termine verlegen, durchgeführte Check-Ups eintragen (wie die Check-Up-Karte am Lead).
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, CheckCircle2, Clock } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const berlinHeute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const fehler = (e, text) => toast.error(e?.response?.data?.detail || text);

function TerminFormular({ datum, start, knopf, onSenden, onAbbrechen, laeuft }) {
  const [d, setD] = useState(datum);
  const [s, setS] = useState(start);
  const [dauer, setDauer] = useState(30);
  const [nachricht, setNachricht] = useState('');
  return (
    <div className="mt-2 space-y-2 rounded-md border border-border/60 p-2" data-testid="checkup-termin-form">
      <Input type="date" value={d} min={berlinHeute()} onChange={(e) => setD(e.target.value)} className="h-8 text-xs" />
      <div className="grid grid-cols-2 gap-1.5">
        <Input type="time" value={s} onChange={(e) => setS(e.target.value)} className="h-8 text-xs" />
        <select value={dauer} onChange={(e) => setDauer(Number(e.target.value))} className="h-8 rounded-md border border-input bg-background px-1 text-xs">
          {[30, 45, 60].map((m) => <option key={m} value={m}>{m} Min.</option>)}
        </select>
      </div>
      <Input value={nachricht} onChange={(e) => setNachricht(e.target.value)} placeholder="Nachricht ans Mitglied (optional)" className="h-8 text-xs" />
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7 text-xs flex-1" disabled={laeuft || !d || !s} onClick={() => onSenden({ datum: d, start: s, dauer, nachricht })}
          data-testid="checkup-termin-senden">{knopf}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onAbbrechen}>Abbrechen</Button>
      </div>
    </div>
  );
}

export function CheckupZeile({ c, darfBearbeiten, onChange }) {
  const navigate = useNavigate();
  const [modus, setModus] = useState(null);           // freigeben | ablehnen | verlegen
  const [hinweis, setHinweis] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const basis = `/public/family/admin/checkup-requests/${c.id}`;

  const ausfuehren = async (pfad, body, ok) => {
    setLaeuft(true);
    try {
      await api.post(`${basis}/${pfad}`, body);
      toast.success(ok);
      setModus(null);
      onChange?.();
    } catch (e) { fehler(e, 'Hat nicht geklappt'); } finally { setLaeuft(false); }
  };
  const durchgefuehrt = async () => {
    setLaeuft(true);
    try {
      await api.post(`/public/family/admin/checkup/${c.profil_id}`, {});
      toast.success(`Check-Up für ${c.profil_name} eingetragen`);
      onChange?.();
    } catch (e) { fehler(e, 'Check-Up nicht eingetragen'); } finally { setLaeuft(false); }
  };

  const offen = c.status === 'offen';
  const erledigt = c.status === 'erledigt';
  const farbe = offen ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
    : erledigt ? 'border-green-200 dark:border-green-900/40 bg-green-50/30 dark:bg-green-900/10'
    : 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10';
  return (
    <div className={`p-2.5 rounded-lg border ${farbe}`} data-testid={`cal-checkup-${c.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-emerald-100 dark:bg-emerald-900/30">
          {erledigt ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : offen ? <Clock className="w-3.5 h-3.5 text-amber-600" /> : <Stethoscope className="w-3.5 h-3.5 text-emerald-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium">{offen ? 'Check-Up-Anfrage' : 'Check-Up'}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded ${offen ? 'bg-amber-200/70 text-amber-900 dark:bg-amber-800/40 dark:text-amber-200' : erledigt ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
              {offen ? 'wartet auf Freigabe' : erledigt ? 'durchgeführt' : 'bestätigt'}
            </span>
          </div>
          <button type="button" onClick={() => navigate(`/leads/${c.profil_id}`)} className="text-[11px] text-muted-foreground mt-0.5 font-medium hover:underline text-left">
            {c.profil_name}
          </button>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {offen ? `Wunsch: ${c.zeitfenster_text || c.zeitfenster}` : c.start ? `${c.start}${c.ende ? `–${c.ende}` : ''} Uhr` : c.termin}
            {!offen && c.decided_by_name ? ` · freigegeben von ${c.decided_by_name}` : ''}
            {!offen && !erledigt ? ` · ${c.trainer_name ? `Trainer: ${c.trainer_name}` : 'noch kein Trainer'}` : ''}
          </p>
          {c.notiz && <p className="text-[10px] text-muted-foreground/80 mt-0.5 line-clamp-2">„{c.notiz}“</p>}

          {darfBearbeiten && offen && !modus && (
            <div className="flex gap-1.5 mt-2">
              <Button size="sm" className="h-7 text-xs" onClick={() => setModus('freigeben')} data-testid={`checkup-freigeben-${c.id}`}>Freigeben</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setModus('ablehnen')}>Ablehnen</Button>
            </div>
          )}
          {darfBearbeiten && c.status === 'bestaetigt' && !modus && (
            <div className="flex gap-1.5 mt-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setModus('verlegen')}>Verlegen</Button>
              {c.datum <= berlinHeute() && (
                <Button size="sm" className="h-7 text-xs" disabled={laeuft} onClick={durchgefuehrt} data-testid={`checkup-durchgefuehrt-${c.id}`}>Durchgeführt</Button>
              )}
            </div>
          )}
          {modus === 'freigeben' && (
            <TerminFormular datum={c.datum} start={c.vorschlag || '10:00'} knopf="Termin freigeben" laeuft={laeuft} onAbbrechen={() => setModus(null)}
              onSenden={(b) => ausfuehren('freigeben', b, `Check-Up für ${c.profil_name} freigegeben`)} />
          )}
          {modus === 'verlegen' && (
            <TerminFormular datum={c.datum} start={c.start || '10:00'} knopf="Termin verlegen" laeuft={laeuft} onAbbrechen={() => setModus(null)}
              onSenden={(b) => ausfuehren('verlegen', b, 'Termin verlegt')} />
          )}
          {modus === 'ablehnen' && (
            <div className="mt-2 space-y-2 rounded-md border border-border/60 p-2">
              <Input value={hinweis} onChange={(e) => setHinweis(e.target.value)} placeholder="Hinweis ans Mitglied (optional)" className="h-8 text-xs" />
              <div className="flex gap-1.5">
                <Button size="sm" variant="destructive" className="h-7 text-xs flex-1" disabled={laeuft}
                  onClick={() => ausfuehren('ablehnen', { nachricht: hinweis }, 'Anfrage abgelehnt')}>Ablehnen</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setModus(null)}>Abbrechen</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Check-Up-Termin vom Team ohne App-Anfrage (15.10.2026): Person suchen (wie am Empfang), Tag/Uhrzeit, Benachrichtigung
export function CheckupTerminAnlegen({ datum, start, onGespeichert, onAbbrechen }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [person, setPerson] = useState(null);
  const [melden, setMelden] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const suchen = async (wert) => {
    setQ(wert); setPerson(null);
    if (wert.trim().length < 2) { setTreffer([]); return; }
    try { const r = await api.get('/checkins/suche', { params: { q: wert.trim(), limit: 6 } }); setTreffer(r.data.treffer || []); } catch { setTreffer([]); }
  };
  const senden = async (b) => {
    if (!person) { toast.error('Bitte erst die Person wählen'); return; }
    setLaeuft(true);
    try {
      await api.post('/public/family/admin/checkup-termine', { profil_id: person.id, ...b, melden });
      toast.success(`Check-Up für ${person.name} eingetragen`);
      onGespeichert?.();
    } catch (e) { fehler(e, 'Check-Up nicht eingetragen'); } finally { setLaeuft(false); }
  };
  return (
    <div className="rounded-lg border border-emerald-300 dark:border-emerald-800 p-2.5 space-y-2 text-xs" data-testid="checkup-termin-neu">
      <p className="font-semibold">Check-Up am {new Date(`${datum}T12:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</p>
      {person ? (
        <div className="flex items-center justify-between rounded-md bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1.5">
          <span className="font-medium">{person.name}</span>
          <button type="button" className="text-[11px] text-muted-foreground hover:underline" onClick={() => setPerson(null)}>ändern</button>
        </div>
      ) : (
        <>
          <Input value={q} onChange={(e) => suchen(e.target.value)} placeholder="Mitglied suchen …" className="h-8 text-xs" data-testid="checkup-person-suche" />
          {treffer.map((p) => (
            <button key={p.id} type="button" onClick={() => { setPerson(p); setTreffer([]); }}
              className="w-full text-left rounded-md px-2 py-1 hover:bg-muted text-xs">
              {p.name}{!p.mitglied && <span className="text-muted-foreground"> · kein Mitglied</span>}
            </button>
          ))}
        </>
      )}
      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <input type="checkbox" checked={melden} onChange={(e) => setMelden(e.target.checked)} /> Mitglied per Push und Mail benachrichtigen
      </label>
      <TerminFormular datum={datum} start={start} knopf="Check-Up eintragen" laeuft={laeuft} onAbbrechen={onAbbrechen} onSenden={senden} />
    </div>
  );
}
